import type { ProcessingLimits } from './types';
import type { XmlSafetyLimits } from '../xml/safeXml';

const MIB = 1024 * 1024;

export function createProcessingLimits(
  profile: ProcessingLimits['profile'],
): ProcessingLimits {
  if (profile === 'mobile') {
    return {
      profile,
      maxFileBytes: 64 * MIB,
      maxEntries: 5_000,
      maxExpandedBytes: 256 * MIB,
      maxEntryBytes: 48 * MIB,
      maxCompressionRatio: 120,
      maxXmlBytes: 16 * MIB,
      maxXmlDepth: 96,
      maxXmlTokens: 300_000,
      maxDocumentXmlTokens: 1_000_000,
      maxXmlAttributes: 64,
      maxXmlTextChars: 12 * MIB,
      maxDocumentXmlTextChars: 32 * MIB,
      maxWorksheets: 512,
      maxRelationships: 20_000,
      maxSharedStrings: 100_000,
      maxWorksheetCells: 75_000,
      maxWorkbookCells: 150_000,
      maxWordParagraphs: 40_000,
      maxWordTables: 2_000,
      maxWordTrackedChanges: 50_000,
      maxOdtParagraphs: 40_000,
      maxOdtTables: 2_000,
      maxOdtListItems: 60_000,
      maxPresentationSlides: 512,
      maxSlideShapes: 10_000,
      maxPresentationShapes: 50_000,
      maxPdfObjects: 40_000,
      maxPdfStreams: 10_000,
      maxPdfPages: 2_000,
      maxPdfObjectBytes: 24 * MIB,
      maxPdfDecodedStreamBytes: 24 * MIB,
      maxPdfTotalDecodedBytes: 96 * MIB,
      maxPdfTextChars: 12 * MIB,
      maxPreviewRows: 100,
    };
  }
  if (profile === 'desktop') {
    return {
      profile,
      maxFileBytes: 512 * MIB,
      maxEntries: 20_000,
      maxExpandedBytes: 1_024 * MIB,
      maxEntryBytes: 192 * MIB,
      maxCompressionRatio: 250,
      maxXmlBytes: 64 * MIB,
      maxXmlDepth: 160,
      maxXmlTokens: 1_200_000,
      maxDocumentXmlTokens: 4_000_000,
      maxXmlAttributes: 128,
      maxXmlTextChars: 48 * MIB,
      maxDocumentXmlTextChars: 128 * MIB,
      maxWorksheets: 2_048,
      maxRelationships: 100_000,
      maxSharedStrings: 400_000,
      maxWorksheetCells: 300_000,
      maxWorkbookCells: 750_000,
      maxWordParagraphs: 200_000,
      maxWordTables: 10_000,
      maxWordTrackedChanges: 250_000,
      maxOdtParagraphs: 200_000,
      maxOdtTables: 10_000,
      maxOdtListItems: 300_000,
      maxPresentationSlides: 2_048,
      maxSlideShapes: 50_000,
      maxPresentationShapes: 300_000,
      maxPdfObjects: 250_000,
      maxPdfStreams: 75_000,
      maxPdfPages: 20_000,
      maxPdfObjectBytes: 128 * MIB,
      maxPdfDecodedStreamBytes: 128 * MIB,
      maxPdfTotalDecodedBytes: 512 * MIB,
      maxPdfTextChars: 64 * MIB,
      maxPreviewRows: 200,
    };
  }
  return {
    profile,
    maxFileBytes: 256 * MIB,
    maxEntries: 10_000,
    maxExpandedBytes: 512 * MIB,
    maxEntryBytes: 96 * MIB,
    maxCompressionRatio: 200,
    maxXmlBytes: 32 * MIB,
    maxXmlDepth: 128,
    maxXmlTokens: 600_000,
    maxDocumentXmlTokens: 2_000_000,
    maxXmlAttributes: 96,
    maxXmlTextChars: 24 * MIB,
    maxDocumentXmlTextChars: 64 * MIB,
    maxWorksheets: 1_024,
    maxRelationships: 50_000,
    maxSharedStrings: 200_000,
    maxWorksheetCells: 150_000,
    maxWorkbookCells: 350_000,
    maxWordParagraphs: 100_000,
    maxWordTables: 5_000,
    maxWordTrackedChanges: 125_000,
    maxOdtParagraphs: 100_000,
    maxOdtTables: 5_000,
    maxOdtListItems: 150_000,
    maxPresentationSlides: 1_024,
    maxSlideShapes: 25_000,
    maxPresentationShapes: 150_000,
    maxPdfObjects: 100_000,
    maxPdfStreams: 30_000,
    maxPdfPages: 8_000,
    maxPdfObjectBytes: 64 * MIB,
    maxPdfDecodedStreamBytes: 64 * MIB,
    maxPdfTotalDecodedBytes: 256 * MIB,
    maxPdfTextChars: 32 * MIB,
    maxPreviewRows: 200,
  };
}

export function inferProcessingProfile(): ProcessingLimits['profile'] {
  const width = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const cores = typeof navigator === 'undefined' ? 4 : navigator.hardwareConcurrency || 4;
  if (width < 768 || cores <= 2) return 'mobile';
  if (width >= 1440 && cores >= 8) return 'desktop';
  return 'balanced';
}

export function xmlSafetyLimits(limits: ProcessingLimits): XmlSafetyLimits {
  return {
    maxDepth: limits.maxXmlDepth,
    maxTokens: limits.maxXmlTokens,
    maxAttributes: limits.maxXmlAttributes,
    maxTextChars: limits.maxXmlTextChars,
    operationBudget: {
      remainingTokens: limits.maxDocumentXmlTokens,
      remainingTextChars: limits.maxDocumentXmlTextChars,
    },
  };
}
